import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "island-glow-cart-v1";

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        if (typeof window === "undefined") return [];
        try {
            const raw = window.localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [donate, setDonate] = useState(() => {
        if (typeof window === "undefined") return false;
        return window.localStorage.getItem("island-glow-donate") === "1";
    });
    const [open, setOpen] = useState(false);

    useEffect(() => {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    useEffect(() => {
        window.localStorage.setItem("island-glow-donate", donate ? "1" : "0");
    }, [donate]);

    const addItem = (product, quantity = 1) => {
        setItems((prev) => {
            const existing = prev.find((l) => l.slug === product.slug);
            if (existing) {
                return prev.map((l) =>
                    l.slug === product.slug
                        ? { ...l, quantity: Math.min(20, l.quantity + quantity) }
                        : l,
                );
            }
            return [
                ...prev,
                {
                    slug: product.slug,
                    name: product.name,
                    price: product.price,
                    image: product.image,
                    quantity,
                },
            ];
        });
        setOpen(true);
    };

    const updateQty = (slug, quantity) => {
        setItems((prev) =>
            prev
                .map((l) =>
                    l.slug === slug
                        ? { ...l, quantity: Math.max(0, Math.min(20, quantity)) }
                        : l,
                )
                .filter((l) => l.quantity > 0),
        );
    };

    const removeItem = (slug) => {
        setItems((prev) => prev.filter((l) => l.slug !== slug));
    };

    const clearCart = () => setItems([]);

    const { subtotal, count, donation, total } = useMemo(() => {
        const sub = items.reduce(
            (acc, l) => acc + Number(l.price) * l.quantity,
            0,
        );
        const ct = items.reduce((acc, l) => acc + l.quantity, 0);
        const don = donate ? Math.round(sub * 10) / 100 : 0;
        return {
            subtotal: Math.round(sub * 100) / 100,
            count: ct,
            donation: don,
            total: Math.round((sub + don) * 100) / 100,
        };
    }, [items, donate]);

    const value = {
        items,
        count,
        subtotal,
        donation,
        total,
        donate,
        setDonate,
        addItem,
        updateQty,
        removeItem,
        clearCart,
        open,
        setOpen,
    };

    return (
        <CartContext.Provider value={value}>{children}</CartContext.Provider>
    );
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
}

export function formatUSD(n) {
    return `$${Number(n).toFixed(2)}`;
}
